import { Box, Button } from '@factorialco/deprecated-design-system'

import PopoverMenu from 'modules/core/components/PopoverMenu'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import i18n from 'modules/core/lib/i18n'

import { useMarkAsPaidPaymentExecution } from 'modules/banking/hooks/useMarkAsPaidPaymentExecution'
import { usePayBankingPayment } from 'modules/banking/hooks/usePayBankingPayment'
import { useRemoveBulkPayment } from 'modules/banking/hooks/useRemoveBulkPayment'
import { useUpdateSepaFile } from 'modules/banking/hooks/useUpdateSepaFile'
import {
  lastPaymentExecution,
  shouldShowPayWithSepaTransfer,
  supportsSepaPayment,
} from 'modules/banking/lib/paymentHelpers'

import type { Payment } from './types'

interface PaymentActionsProps {
  payment: Payment
  canSeeSepaTransferAction: boolean
}

interface DownloadSepaActionProps {
  paymentExecution: Payment['paymentExecutions'][number]
}

const DownloadSepaAction = ({ paymentExecution }: DownloadSepaActionProps) => {
  const { updateSepaFile } = useUpdateSepaFile()
  const label = i18n.t('banking.payments.download_sepa_file')

  if (paymentExecution.sepaFile?.url) {
    return <PopoverMenu.HrefOption href={paymentExecution.sepaFile.url} title={label} download />
  } else {
    return (
      <PopoverMenu.TouchableOption
        title={label}
        onClick={() => updateSepaFile(paymentExecution.id)}
      />
    )
  }
}

export const Actions = ({ payment, canSeeSepaTransferAction }: PaymentActionsProps) => {
  const { navigation } = useNavigation()
  const paymentExecution = lastPaymentExecution(payment)
  const { submitRemoveBulkPayment } = useRemoveBulkPayment()
  const { markAsPaidPaymentExecution } = useMarkAsPaidPaymentExecution()
  const { payPayment } = usePayBankingPayment()

  const onRemoveBulkPayment = async () => {
    await submitRemoveBulkPayment({ paymentId: payment.id })
  }

  const onMarkAsPaid = async () => {
    if (paymentExecution) {
      await markAsPaidPaymentExecution(paymentExecution.id)
    }
  }

  const onPayWithSepaTransfer = async () => {
    const result = await payPayment(payment.id)
    if (result.success && result.consentUrl) {
      window.location.href = result.consentUrl
    }
  }

  const transfersCount = paymentExecution?.transfersConnection.totalCount || 0

  const supportsSepa = supportsSepaPayment(
    payment.account?.account?.providerConfiguration?.services
  )

  const seeSepaTransferAction = supportsSepa && canSeeSepaTransferAction

  // Show pay actions only if status is pending and bank account supports SEPA payments
  const showPayActions = shouldShowPayWithSepaTransfer(
    payment.status,
    supportsSepa,
    transfersCount > 0
  )

  // Calculate if there are any menu options available to show the popover menu
  const hasMenuOptions =
    (seeSepaTransferAction && showPayActions) ||
    !paymentExecution ||
    !transfersCount ||
    (!seeSepaTransferAction && paymentExecution) ||
    (!seeSepaTransferAction && paymentExecution && paymentExecution.status === 'in_progress')

  return (
    <Box gap="s4" flexDirection="row" justifyContent="flexEnd">
      {hasMenuOptions && (
        <PopoverMenu type="list">
          <PopoverMenu.Trigger>
            <Button.Decorative type="secondary" iconName="ellipsis" />
          </PopoverMenu.Trigger>
          <PopoverMenu.Content side="bottom" align="start">
            {seeSepaTransferAction && showPayActions && (
              <>
                <PopoverMenu.TouchableOption
                  title={i18n.t('banking.payments.pay_with_sepa_transfer')}
                  onClick={onPayWithSepaTransfer}
                />
              </>
            )}
            {(!paymentExecution || !transfersCount) && (
              <PopoverMenu.TouchableOption
                title={i18n.t('banking.payments.delete_bulk_payment')}
                onClick={onRemoveBulkPayment}
              />
            )}
            {!seeSepaTransferAction && paymentExecution && (
              <DownloadSepaAction paymentExecution={paymentExecution} />
            )}
            {!seeSepaTransferAction &&
              paymentExecution &&
              paymentExecution.status === 'in_progress' && (
                <PopoverMenu.TouchableOption
                  title={i18n.t('banking.payments.mark_as_paid')}
                  onClick={onMarkAsPaid}
                />
              )}
          </PopoverMenu.Content>
        </PopoverMenu>
      )}
      <Button.Router
        to={navigation.finance.banking.payments.paymentId.fullPath({ id: payment.id })}
        type="secondary"
        iconName="arrow-right"
        disabled={!paymentExecution || !transfersCount}
      />
    </Box>
  )
}
